/**
 * Suite model access — shared law with BookForge / TextForge / ImageForge / VoiceForge.
 */

export const SUITE_MEMBER_PRICE_USD = 2.99;
export const FREE_MAX_PARAMS_B = 9;
export const CREDITS_MAX_PARAMS_B = 85;
export const CREDITS_LAUNCH_MIN_USD = 0.05;

export type ModelGate = "open" | "credits" | "member";

export interface ModelAccessResult {
  allowed: boolean;
  gate: ModelGate;
  badge: "Member" | "Credits" | null;
  message: string | null;
}

export function isCloudflareSku(s: {
  provider?: string;
  tags?: string[];
  offer?: { id?: string; provider?: string };
  hardwareId?: string;
  modelId?: string;
  cloudflare?: boolean;
}): boolean {
  if (s.cloudflare) return true;
  if ((s.provider || "").toLowerCase().includes("cloudflare")) return true;
  if ((s.offer?.provider || "").toLowerCase().includes("cloudflare")) return true;
  const tags = (s.tags || []).map((t) => t.toLowerCase());
  if (tags.some((t) => t.includes("cloudflare") || t === "cf" || t === "one-bill")) {
    return true;
  }
  const oid = String(s.offer?.id || "");
  if (oid.startsWith("cf_inf_") || oid.startsWith("cf_")) return true;
  if (s.hardwareId === "forge-cf") return true;
  const mid = String(s.modelId || "").toLowerCase();
  if (mid.startsWith("@cf/") || mid.includes("workers-ai")) return true;
  return false;
}

export function canAccessSku(opts: {
  paramsB?: number | null;
  isMember: boolean;
  balanceUsd?: number;
  sku: {
    provider?: string;
    tags?: string[];
    offer?: { id?: string; provider?: string };
    hardwareId?: string;
    modelId?: string;
    cloudflare?: boolean;
  };
}): ModelAccessResult {
  if (opts.isMember) {
    return { allowed: true, gate: "member", badge: null, message: null };
  }

  if (isCloudflareSku(opts.sku)) {
    return {
      allowed: false,
      gate: "member",
      badge: "Member",
      message: `Cloudflare models need Suite Member ($${SUITE_MEMBER_PRICE_USD}/mo).`,
    };
  }

  const params = opts.paramsB ?? 0;
  if (params <= FREE_MAX_PARAMS_B) {
    return { allowed: true, gate: "open", badge: null, message: null };
  }

  if (params <= CREDITS_MAX_PARAMS_B) {
    const bal = opts.balanceUsd ?? 0;
    if (bal >= CREDITS_LAUNCH_MIN_USD) {
      return { allowed: true, gate: "credits", badge: "Credits", message: null };
    }
    return {
      allowed: false,
      gate: "credits",
      badge: "Credits",
      message: "Add suite credits (min $5) to rent mid-size models.",
    };
  }

  return {
    allowed: false,
    gate: "member",
    badge: "Member",
    message: `Models above ~${CREDITS_MAX_PARAMS_B}B need Suite Member ($${SUITE_MEMBER_PRICE_USD}/mo).`,
  };
}
